import type { APC } from 'api/v2/users/APC';
import getAPCs, { apcToOption, groupAPCs } from 'api/v2/users/APC';
import useCustomerCountry from 'hooks/shared/useCustomerCountry';
import type { ReactNode } from 'react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'react-select';
import { components } from 'react-select';
import type { AvailableCountries, AvailableLanguages } from 'types/users.types';
import { Select } from '../Select';
import { getOptionsDict } from '../Select/helpers';
import type { SelectProps } from '../Select/select.types';

export type ProfessionSelectProps = Omit<SelectProps, 'value'> & {
  value?: APC | string | null;
  renderFooter?: (menuProps: MenuProps) => ReactNode;
  country?: AvailableCountries;
  lang?: AvailableLanguages;
};

const ProfessionSelect = (props: ProfessionSelectProps) => {
  const { t, i18n } = useTranslation();

  const language = props.lang || (i18n.language as AvailableLanguages);
  const _country = useCustomerCountry();
  const country = props.country || (_country as AvailableCountries);

  const renderFn = useRef(props.renderFooter);
  renderFn.current = props.renderFooter;

  const Menu = useCallback((props: MenuProps) => {
    return (
      <components.Menu {...props}>
        {props.children}
        {renderFn.current?.(props)}
      </components.Menu>
    );
  }, []);

  const value = props.value;

  const isValueFreeText = typeof value === 'string';

  const APC1 = isValueFreeText ? null : value?.APC1;
  const APC2 = isValueFreeText ? null : value?.APC2;

  return (
    <Select
      cacheKey={`${language}-${country}`}
      placeholder={t('signup.profession.dropdown_field.placeholder')}
      mode="asyncOptions"
      asyncSearch
      fetchOnEmpty
      components={{ Menu }}
      openMenuOnFocus
      openMenuOnClick
      noOptionsMessage={() => t('list_no_result')}
      loadingMessage={() => null}
      optionsDict={
        APC1 && APC2 ? getOptionsDict([apcToOption({ APC1, APC2 })]) : {}
      }
      formatGroupLabel={
        (group: any) => (
          <span className="uppercase">
            {t(`${country}.APC1.${group.label}`)}
          </span>
        )
        //   (
        //   <div className="text-init text-primary-700 text-sm font-semibold leading-tight border-b border-primary-50 py-2">
        //     {t(`${country}.APC1.${group.label}`)}
        //   </div>
        // )
      }
      formatOptionLabel={(option: any, meta) => {
        if (meta.context === 'value') {
          if (option.__isCreated) {
            if (APC2) return t(`${country}.APC2.${APC2}`);
            return option.value;
          }
          return t(`${country}.APC2.${option.label}`);
        }
        return option.__isCreated
          ? option.value
          : t(`${country}.APC2.${option.label}`);
        // return (
        //   <div className="text-init text-primary-700 text-sm leading-tight">
        //     {option.__isCreated
        //       ? option.value
        //       : t(`${country}.APC2.${option.label}`)}
        //   </div>
        // );
      }}
      onFetch={(search = '') => {
        return getAPCs({
          country,
          language,
          name: search as string,
          page: 1,
          perPage: 9999, // everything
        }).then((data) => groupAPCs(data.data));
      }}
      {...props}
      value={APC1 && APC2 ? `${APC1}@${APC2}` : isValueFreeText ? value : null}
    />
  );
};

export default ProfessionSelect;
